import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTrackPerformanceQuery } from "@/api/vercel";
import { format } from "date-fns";
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function StateResults() {
  const { state, slug } = useParams();
  const resolvedState = state ?? slug ?? "";
  const [tracks, setTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "totalRaces",
    direction: "desc",
  });

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedTracks = [...tracks].sort((a, b) => {
      if (typeof a[key] === "number") {
        return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
      }
      return direction === "asc"
        ? a[key].localeCompare(b[key])
        : b[key].localeCompare(a[key]);
    });
    setTracks(sortedTracks);
  };

  // Load track names data
  const [trackNames, setTrackNames] = useState({});
  useEffect(() => {
    async function loadTrackNames() {
      try {
        const response = await fetch(
          "/data/horseracing/tracks/track-names.json",
        );
        const data = await response.json();
        setTrackNames(data);
      } catch (err) {
        console.error("Error loading track names:", err);
      }
    }
    loadTrackNames();
  }, []);

  // Use the track performance query hook (same as analysis-overview page)
  const {
    data: trackPerformanceData,
    isLoading: isLoadingResults,
    error: resultsError,
  } = useTrackPerformanceQuery({
    // Must exceed the total number of tracks with performance data (~210) so
    // state results aren't computed from a truncated set.
    limit: 1000,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: true,
  });

  // Process tracks for the selected state
  const processedTracks = useMemo(() => {
    if (!trackPerformanceData?.data || Object.keys(trackNames).length === 0) {
      return [];
    }

    const trackStats = new Map();

    trackPerformanceData.data.forEach((track) => {
      // Skip if no models data
      if (!track.models?.HayiAuto) return;

      const trackData = track.models.HayiAuto;
      const trackName = track.id;

      // Check if track belongs to current state
      // Handle both direct track names and alternate names (like Callaghan Park for Rockhampton)
      const trackInfo =
        trackNames[trackName] ||
        Object.values(trackNames).find((t) => t.alt === trackName);
      if (
        !trackInfo ||
        trackInfo.State.toLowerCase() !== resolvedState.toLowerCase()
      )
        return;

      // Calculate total stats
      const totalBets =
        trackData.early.totalBets + trackData.raceTime.totalBets;
      const totalWins =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + pos.wins,
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + pos.wins,
          0,
        );
      const avgROI = (trackData.early.roi + trackData.raceTime.roi) / 2;
      const totalReturns =
        trackData.early.totalReturns + trackData.raceTime.totalReturns;
      const netReturns = totalReturns - totalBets;

      trackStats.set(trackName, {
        name: trackName,
        totalRaces: trackData.totalRaces,
        wins: totalWins,
        roi: avgROI,
        totalBets: totalBets,
        totalReturns: totalReturns,
        netReturns: netReturns,
        early: trackData.early,
        raceTime: trackData.raceTime,
      });
    });

    // Convert to array and sort by total races (descending)
    return Array.from(trackStats.values()).sort(
      (a, b) => b.totalRaces - a.totalRaces,
    );
  }, [trackPerformanceData, trackNames, resolvedState]);

  // Update tracks state when processedTracks changes
  useEffect(() => {
    if (processedTracks.length > 0) {
      setTracks(processedTracks);
      setIsLoading(false);
    }
  }, [processedTracks]);

  if (isLoading || isLoadingResults) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error || resultsError) {
    return (
      <div className="p-4">
        <Card className="p-4 text-center text-red-500">
          {error || "Error loading track performance data"}
        </Card>
      </div>
    );
  }

  const stateDisplayName =
    {
      qld: "Queensland",
      nsw: "New South Wales",
      vic: "Victoria",
      sa: "South Australia",
      wa: "Western Australia",
      tas: "Tasmania",
      nt: "Northern Territory",
      act: "Australian Capital Territory",
    }[resolvedState.toLowerCase()] || resolvedState;

  return (
    <>
      <Helmet>
        <title>
          {stateDisplayName} Horse Racing Results & Performance | Lexi Tips
        </title>
        <meta
          name="description"
          content={`View detailed horse racing results and performance statistics for all tracks in ${stateDisplayName}. Track-by-track analysis of wins, places, and ROI.`}
        />
        <meta
          name="keywords"
          content={`${stateDisplayName} horse racing, racing results, track performance, racing tips, ${resolvedState} racing statistics`}
        />
      </Helmet>

      <div className="mt-16 mb-6">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <Link
            to="/analysis"
            className="hover:text-[#F30099] transition-colors"
          >
            Analysis
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{stateDisplayName}</span>
        </nav>
      </div>

      <h1 className="text-2xl font-bold mb-6">
        {stateDisplayName} Racing Results
      </h1>

      <div className="grid gap-6 mb-8">
        <Card className="p-6 border border-[#F30099]/20 shadow-md">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Track Performance Overview
          </h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("name")}
                  >
                    Track{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("totalRaces")}
                  >
                    Total Races{" "}
                    {sortConfig.key === "totalRaces" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("wins")}
                  >
                    Wins{" "}
                    {sortConfig.key === "wins" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("totalBets")}
                  >
                    Total Bets{" "}
                    {sortConfig.key === "totalBets" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("netReturns")}
                  >
                    Net Returns{" "}
                    {sortConfig.key === "netReturns" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead
                    className="text-left cursor-pointer"
                    onClick={() => sortData("roi")}
                  >
                    ROI{" "}
                    {sortConfig.key === "roi" &&
                      (sortConfig.direction === "asc" ? "↑" : "↓")}
                  </TableHead>
                  <TableHead className="text-left">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tracks.map((track) => (
                  <TableRow key={track.name}>
                    <TableCell>{track.name}</TableCell>
                    <TableCell>{track.totalRaces.toLocaleString()}</TableCell>
                    <TableCell>{track.wins.toLocaleString()}</TableCell>
                    <TableCell>{track.totalBets.toLocaleString()}</TableCell>
                    <TableCell
                      className={
                        track.netReturns > 0 ? "text-green-600" : "text-red-600"
                      }
                    >
                      $
                      {track.netReturns.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell
                      className={
                        track.roi > 0 ? "text-green-600" : "text-red-600"
                      }
                    >
                      {track.roi.toLocaleString(undefined, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}
                      %
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/analysis/${resolvedState.toLowerCase()}/${track.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-[#F30099] hover:text-[#F30099]/80"
                      >
                        View Details
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </>
  );
}
